import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { loadAllMarkdown } from '../utils/markdownLoader.js';
import StaticSeo from '../components/StaticSeo.jsx';
import { toPlainText } from '../utils/plainText.js';
import BLOG_IMAGES from '../data/blog-images.json'; // s139 Phase 7a

// ─────────────────────────────────────────────────────────────────────────
// BLOG INDEX
// Reverse-chronological blog feed. Newspaper-vs-magazine register against
// the editorial articles: Fraunces upright (not italic), tighter cards,
// dateline kicker, category chips, denser grid.
// ─────────────────────────────────────────────────────────────────────────

// Lazy glob — blog posts are fetched in parallel on mount.
const blogModules = import.meta.glob('../blog/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

function parseFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!match) return {};
  const yamlText = match[1];
  const fm = {};
  let currentKey = null;

  for (const line of yamlText.split('\n')) {
    if (line.trim() === '' || line.trim().startsWith('#')) continue;

    const topMatch = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (topMatch && !line.startsWith(' ')) {
      currentKey = topMatch[1];
      const value = topMatch[2].trim();
      if (value === '') {
        fm[currentKey] = null;
      } else if (value.startsWith('"') && value.endsWith('"')) {
        fm[currentKey] = value.slice(1, -1);
      } else if (value === 'true' || value === 'false') {
        fm[currentKey] = value === 'true';
      } else {
        fm[currentKey] = value;
      }
      continue;
    }

    const subMatch = line.match(/^  ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (subMatch) {
      const subKey = subMatch[1];
      const subValue = subMatch[2].trim();
      if (!fm[currentKey] || typeof fm[currentKey] !== 'object' || Array.isArray(fm[currentKey])) {
        fm[currentKey] = {};
      }
      fm[currentKey][subKey] = subValue.startsWith('"') && subValue.endsWith('"')
        ? subValue.slice(1, -1)
        : subValue;
      continue;
    }

    const arrMatch = line.match(/^  - (.*)$/);
    if (arrMatch) {
      const item = arrMatch[1].trim();
      if (!Array.isArray(fm[currentKey])) fm[currentKey] = [];
      fm[currentKey].push(item.startsWith('"') && item.endsWith('"') ? item.slice(1, -1) : item);
    }
  }
  return fm;
}

const CATEGORY_LABELS = {
  seasonal: 'Seasonal',
  industry: 'Industry',
  'how-to': 'How-to',
  opinion: 'Opinion',
  product: 'Product',
};

const CATEGORY_ORDER = ['all', 'seasonal', 'industry', 'how-to', 'opinion', 'product'];

export default function BlogIndex() {
  const [dark, setDark] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [allPosts, setAllPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadAllMarkdown(blogModules, parseFrontmatter).then((entries) => {
      if (cancelled) return;
      const list = entries
        .map((e) => e.frontmatter || {})
        .filter((fm) => fm.slug)
        // Reverse-chronological
        .sort((a, b) => (b.publishedDate || '').localeCompare(a.publishedDate || ''));
      setAllPosts(list);
    });
    return () => { cancelled = true; };
  }, []);

  const visiblePosts = useMemo(() => {
    return allPosts.filter((p) => {
      if (activeCategory !== 'all' && p.category !== activeCategory) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        const hay = `${p.title} ${p.excerpt} ${(p.tags || []).join(' ')} ${p.author?.name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allPosts, activeCategory, query]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
  };

  // The first post (most recent) becomes a hero card; the rest go into the grid.
  const hero = visiblePosts[0];
  const rest = visiblePosts.slice(1);

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <StaticSeo schemaType="collectionPage" path="/blog" />
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .reveal { animation: fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both; }

        .blog-card { transition: background 0.18s, border-color 0.18s; cursor: pointer; }
        .blog-card:hover { background: ${T.surface}; border-color: ${T.sienna}; }
        .blog-card:hover .blog-arrow { transform: translateX(4px); color: ${T.sienna}; }
        .blog-arrow { transition: transform 0.18s, color 0.18s; }

        .hero-card { transition: background 0.18s; cursor: pointer; }
        .hero-card:hover { background: ${T.surface}; }
        .hero-card:hover .hero-title { color: ${T.sienna}; }
        .hero-title { transition: color 0.18s; }

        .chip { transition: background 0.15s, color 0.15s, border-color 0.15s; cursor: pointer; }
        .chip:hover { border-color: ${T.sienna}; color: ${T.ink}; }
        .chip-active { background: ${T.ink}; color: ${T.bg}; border-color: ${T.ink}; }

        .ghost-link:hover { color: ${T.sienna}; }

        @media (max-width: 820px) {
          .blog-grid { grid-template-columns: 1fr !important; }
          .hero-grid { grid-template-columns: 1fr !important; }
          .container { padding: 24px 20px 60px !important; }
          .blog-h1 { font-size: 56px !important; }
        }
      `}</style>

      <div className="container" style={{ maxWidth: 1080, margin: '0 auto', padding: '32px 28px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 24, letterSpacing: '-0.015em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Dateline */}
        <div className="reveal" style={{
          display: 'flex', justifyContent: 'space-between',
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.inkFaint,
          marginBottom: 48,
        }}>
          <span>§ THE BLOG</span>
          <span>{allPosts.length} {allPosts.length === 1 ? 'POST' : 'POSTS'} · UPDATED WEEKLY</span>
        </div>

        {/* Title */}
        <h1 className="blog-h1 reveal" style={{
          fontFamily: fontDisplay, fontSize: 88, fontWeight: 400,
          lineHeight: 1.0, letterSpacing: '-0.03em',
          margin: '0 0 16px',
        }}>
          The <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>weekly column</em>.
        </h1>
        <p className="reveal" style={{
          fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
          lineHeight: 1.55, color: T.inkSoft,
          margin: '0 0 56px', maxWidth: 640,
        }}>
          Seasonal pieces, product tests, contrarian takes, and the hacks we keep seeing on the internet that we keep having to unwind. Lighter than the encyclopedia. Still the same chemistry.
        </p>

        {/* Filters + search */}
        <div className="reveal" style={{
          display: 'flex', gap: 16, alignItems: 'center',
          flexWrap: 'wrap', justifyContent: 'space-between',
          paddingBottom: 24, marginBottom: 40,
          borderBottom: `1px solid ${T.rule}`,
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {CATEGORY_ORDER.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`chip ${isActive ? 'chip-active' : ''}`}
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    background: isActive ? T.ink : 'transparent',
                    color: isActive ? T.bg : T.inkSoft,
                    border: `1px solid ${isActive ? T.ink : T.rule}`,
                    padding: '8px 14px', borderRadius: 0,
                  }}
                >
                  {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
                </button>
              );
            })}
          </div>
          <input
            type="text"
            placeholder="Search the column…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              fontFamily: fontBody, fontSize: 14,
              background: 'transparent', color: T.ink,
              border: `1px solid ${T.rule}`,
              padding: '8px 14px', minWidth: 220,
              outline: 'none',
            }}
            onFocus={(e) => { e.target.style.borderColor = T.sienna; }}
            onBlur={(e) => { e.target.style.borderColor = T.rule; }}
          />
        </div>

        {/* Results */}
        {visiblePosts.length === 0 ? (
          <p style={{
            fontFamily: fontDisplay, fontSize: 18, fontStyle: 'italic',
            color: T.inkSoft, padding: '40px 0',
          }}>
            Nothing matches that filter yet. Try another category, or clear the search.
          </p>
        ) : (
          <>
            {/* Hero card — most recent post */}
            {hero && (
              <Link
                to={`/blog/${hero.slug}`}
                className="hero-card reveal"
                style={{
                  display: 'block', textDecoration: 'none', color: T.ink,
                  border: `1px solid ${T.rule}`,
                  marginBottom: 40,
                }}
              >
                <div className="hero-grid" style={{
                  display: 'grid', gridTemplateColumns: '180px 1fr',
                  gap: 0,
                }}>
                  <div style={{
                    padding: '32px 24px', borderRight: `1px solid ${T.rule}`,
                    background: T.surface,
                  }}>
                    <div style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 16,
                    }}>
                      LATEST
                    </div>
                    <div style={{
                      fontFamily: fontDisplay, fontSize: 36, fontWeight: 400,
                      lineHeight: 1.0, letterSpacing: '-0.02em',
                      color: T.ink, marginBottom: 8,
                    }}>
                      {formatDate(hero.publishedDate).split(' ')[1]?.replace(',', '')}
                    </div>
                    <div style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', color: T.inkFaint,
                    }}>
                      {formatDate(hero.publishedDate).split(' ').slice(0, 1).join(' ')} {formatDate(hero.publishedDate).split(' ').slice(2).join(' ')}
                    </div>
                  </div>
                  <div style={{ padding: '32px 32px' }}>
                    <div style={{
                      display: 'flex', gap: 12, alignItems: 'center',
                      marginBottom: 16,
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em', color: T.inkFaint,
                    }}>
                      <span style={{ color: T.sienna }}>
                        {CATEGORY_LABELS[hero.category]?.toUpperCase() || 'POST'}
                      </span>
                      <span>·</span>
                      <span>{hero.readTime}</span>
                    </div>
                    <h2 className="hero-title" style={{
                      fontFamily: fontDisplay, fontSize: 36, fontWeight: 400,
                      lineHeight: 1.1, letterSpacing: '-0.02em',
                      color: T.ink, margin: '0 0 16px',
                    }}>
                      {hero.title}
                    </h2>
                    {hero.excerpt && (
                      <p style={{
                        fontFamily: fontDisplay, fontSize: 17, fontWeight: 300,
                        lineHeight: 1.55, color: T.inkSoft,
                        margin: '0 0 20px',
                      }}>
                        {toPlainText(hero.excerpt)}
                      </p>
                    )}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      paddingTop: 16, borderTop: `1px solid ${T.rule}`,
                    }}>
                      <div style={{
                        fontFamily: fontMono, fontSize: 11,
                        letterSpacing: '0.15em', color: T.inkFaint,
                      }}>
                        BY {hero.author?.name?.toUpperCase() || 'STAFF'}
                      </div>
                      <div className="blog-arrow" style={{
                        fontFamily: fontMono, fontSize: 11,
                        letterSpacing: '0.18em', color: T.sienna,
                      }}>
                        READ →
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Card grid — remaining posts */}
            {rest.length > 0 && (
              <div className="blog-grid" style={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 24,
              }}>
                {rest.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="blog-card"
                    style={{
                      display: 'flex', flexDirection: 'column',
                      textDecoration: 'none', color: T.ink,
                      border: `1px solid ${T.rule}`,
                      padding: '24px 24px 22px',
                      minHeight: 240,
                    }}
                  >
                    {BLOG_IMAGES[p.slug] && (
                      <img src={BLOG_IMAGES[p.slug].thumb} alt={BLOG_IMAGES[p.slug].alt} width={480} height={320} loading="lazy" decoding="async"
                        style={{ width: 'calc(100% + 48px)', height: 'auto', aspectRatio: '3 / 2', objectFit: 'cover', display: 'block', margin: '-24px -24px 18px' }} />
                    )}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em',
                      marginBottom: 16,
                    }}>
                      <span style={{ color: T.sienna }}>
                        {CATEGORY_LABELS[p.category]?.toUpperCase() || 'POST'}
                      </span>
                      <span style={{ color: T.inkFaint }}>
                        {formatDate(p.publishedDate)}
                      </span>
                    </div>
                    <h3 style={{
                      fontFamily: fontDisplay, fontSize: 24, fontWeight: 400,
                      lineHeight: 1.15, letterSpacing: '-0.015em',
                      margin: '0 0 12px',
                    }}>
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p style={{
                        fontFamily: fontBody, fontSize: 14,
                        lineHeight: 1.55, color: T.inkSoft,
                        margin: '0 0 20px', flex: 1,
                      }}>
                        {toPlainText(p.excerpt)}
                      </p>
                    )}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      paddingTop: 14, borderTop: `1px solid ${T.rule}`,
                    }}>
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        letterSpacing: '0.15em', color: T.inkFaint,
                      }}>
                        {p.author?.name?.toUpperCase() || 'STAFF'} · {p.readTime}
                      </div>
                      <span className="blog-arrow" style={{
                        fontFamily: fontMono, fontSize: 11, color: T.inkFaint,
                      }}>→</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <footer style={{
          marginTop: 80, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <Link to="/" className="ghost-link" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO HOMEPAGE
          </Link>
          <span style={{
            fontFamily: fontMono, fontSize: 10,
            letterSpacing: '0.15em', color: T.inkFaint,
          }}>
            © 2026 WINDOW WASHING GUIDE
          </span>
        </footer>
      </div>
    </div>
  );
}
